import { createSelector } from '@ngrx/store';

import * as user from '../../auth/actions/user'
import * as auth from '../../auth/actions/auth.actions'
import * as facet from '../actions/facet'
import * as catalog from '../actions/catalog'

import { CombinedFacets, Facet } from '../../facets/models/facet'
import { AppliedFacets } from '../../facets/models/applied-facets'
import { FacetCount } from '../../facets/models/facet-count'
import { PreppedGroups } from '../../facets/models/prepped-groups'

import { hasProps } from '../../core/helpers/object'
import { applyOver, createUXFacetChips, createUXFacetGroups, prepFacetGroups, prepFacets } from '../../facets/helpers/facets';
import { getNewTtl } from '../../core/helpers/date'

export interface State {
    // Init state
    initGroups: boolean
    initFacets: boolean

    // Data
    groups:  PreppedGroups,
    currentGroups: string[]
    groupsTtl: Date

    facets: { [group: string]: Facet[] }
    counts: { [group: string]: FacetCount[] }
    facetsTtl: Date

    applied: AppliedFacets
    openGroups: string[]
}

const initialState: State = {
    initGroups: false,
    initFacets: false,
    groups: {},
    openGroups: [],
    facets: {},
    currentGroups: [],
    counts: {},
    applied: {},
    groupsTtl: null,
    facetsTtl: null
}

export function reducer(
    state = initialState, action: facet.Actions | catalog.Actions | user.Actions | auth.Actions
) {
    switch(action.type){
        case facet.INIT_GROUPS:
            return {
                ...state,
                initGroups: true,
            }

        case facet.GET_GROUPS_SKIPPED:
            return {
              ...state,
              initGroups: false
            }

        case facet.GET_GROUPS_SUCCES:
            let groups = prepFacetGroups(action.payload)

            return {
                ...state,
                groups: groups,
                initGroups: false,
                groupsTtl: getNewTtl()
            }

        case facet.GET_GROUPS_FAILED:
            return {
                ...state,
                initGroups: false
            }

        case facet.INIT_FACETS:
            return {
                ...state,
                initFacets: true
            }

        case facet.GET_FACETS_SKIPPED:
            return {
              ...state,
              initFacets: false
            }

        case facet.GET_FACETS_SUCCESS:
            let facets = prepFacets(action.payload)

            return {
                ...state,
                initFacets: false,
                facets,
                facetsTtl: getNewTtl()
            }

        case facet.GET_FACETS_FAILED:
            return {
                ...state,
                initFacets: false
            }

        case facet.APPLY:
            return {
                ...state,
                applied: action.payload
            }

        case facet.FILTER_SUCCESS:
          let incoming = applyOver(action.payload)

          return {
                ...state,
                counts: incoming,
                currentGroups: Object.keys(incoming)
            }

        case facet.FILTER_RESET:
            return {
              ...state,
              counts: {},
              currentGroups: [],
              facets: {},
              groups: {},
              groupsTtl: null,
              facetsTtl: null
            }

        case facet.SHOW_MORE:
            let group = action.payload

            if(state.openGroups.indexOf(group) !== -1)
                return {
                    ...state,
                    openGroups: state.openGroups.filter(open => open !== group)
                }

            return {
                ...state,
                openGroups: [
                    ...state.openGroups,
                    group
                ]
            }

      case auth.LOGOUT:
        return initialState

        default:
            return state
    }
}

// Public Selectors
export const isInitializing =
    (state: State) => state.initFacets || state.initGroups
export const getFacets = (state: State) => state.counts
export const getGroups = (state: State) => state.groups
export const getApplied = (state: State) => state.applied
export const getBaseline = (state: State) => state.facets
export const getCurrentGroups = (state: State) => state.currentGroups
export const getOpenGroups = (state: State) => state.openGroups
export const isFacetGroupsTtlExpired = (state: State) => state.groupsTtl === null || state.groupsTtl < new Date()
export const isFacetsTtlExpired = (state: State) => state.facetsTtl === null || state.facetsTtl < new Date()

// Complex public selectors
export const hasApplied = createSelector(
    getApplied,
    applied => applied && hasProps(applied)
)
export const hasGroups = createSelector(
    getGroups,
    groups => groups && hasProps(groups)
)
export const hasBaseline = createSelector(
    getBaseline,
    baseline => baseline && hasProps(baseline)
)
export const getFacetsUX = createSelector(
    getCurrentGroups,
    getGroups,
    getBaseline,
    getFacets,
    createUXFacetGroups
)
export const getFacetChips = createSelector(
    getCurrentGroups,
    getFacetsUX,
    getApplied,
    createUXFacetChips
)

export const combinedFacets = createSelector(
  getBaseline,
  getGroups,
  (facets, groups): CombinedFacets => ({facets, groups})
)
